/**
 * One answered question, the way a learner reviews it after submitting:
 * question text (+ figure), every option with the correct one ticked and their own choice marked,
 * the expected answer for typed / numeric questions, marks, and the explanation.
 *
 *   <AnswerReview detail={ad} index={0} />            // detail = an entry of result.answers_detail
 */
import React from 'react';
import RichHtml from '../richtext/RichText';
import { typeLabel } from './QuestionPreview';
import { CheckCircle, XCircle, Circle, Minus, Lightbulb, Info } from '@phosphor-icons/react';

const CHOICE_TYPES = ['MCQ_SINGLE', 'MCQ_MULTI', 'TRUE_FALSE'];

function OptionRow({ option }) {
  const { is_correct: correct, selected } = option;
  const tone = correct ? 'border-emerald-300 bg-emerald-50' : selected ? 'border-red-300 bg-red-50' : 'border-border bg-background';
  const Icon = correct ? CheckCircle : selected ? XCircle : Circle;
  const iconTone = correct ? 'text-emerald-600' : selected ? 'text-red-600' : 'text-muted-foreground/40';
  return (
    <li className={`flex items-start gap-2 rounded border px-2.5 py-1.5 ${tone}`} data-testid={`review-option-${option.id}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconTone}`} weight={correct || selected ? 'fill' : 'regular'} />
      <span className="text-[12px] font-semibold text-muted-foreground w-4 flex-shrink-0">{option.label}</span>
      <RichHtml html={option.text} className="text-[13px] flex-1 min-w-0" />
      <span className="flex flex-wrap gap-1 justify-end flex-shrink-0">
        {selected && <span className={`rounded px-1.5 h-5 inline-flex items-center text-[10px] font-semibold uppercase ${correct ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>Your answer</span>}
        {correct && <span className="rounded px-1.5 h-5 inline-flex items-center text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800">Correct</span>}
      </span>
    </li>
  );
}

function TextAnswers({ detail }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <div className={`rounded border px-2.5 py-1.5 ${detail.is_correct ? 'border-emerald-300 bg-emerald-50' : detail.answered ? 'border-red-300 bg-red-50' : 'border-border bg-muted/40'}`}>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Your answer</p>
        <p className="text-[13px] break-words" data-testid="review-your-answer">{detail.your_answer_text || <span className="text-muted-foreground italic">Not answered</span>}</p>
      </div>
      {detail.correct_answer_text ? (
        <div className="rounded border border-emerald-300 bg-emerald-50 px-2.5 py-1.5">
          <p className="text-[11px] uppercase tracking-wide text-emerald-700">Correct answer</p>
          <p className="text-[13px] break-words" data-testid="review-correct-answer">{detail.correct_answer_text}</p>
        </div>
      ) : (
        <div className="rounded border border-border bg-muted/40 px-2.5 py-1.5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Marking</p>
          <p className="text-[13px] text-muted-foreground">Graded by your instructor</p>
        </div>
      )}
    </div>
  );
}

export default function AnswerReview({ detail, index }) {
  const max = detail.max_points ?? detail.points_possible ?? 0;
  const got = detail.points_awarded ?? detail.points_earned ?? 0;
  const choices = CHOICE_TYPES.includes(detail.question_type) && (detail.options || []).length > 0;
  const unanswered = detail.answered === false;
  const tone = detail.is_correct ? 'border-emerald-200' : unanswered ? 'border-border' : 'border-red-200';

  return (
    <article className={`rounded-lg border ${tone} bg-card overflow-hidden`} data-testid={`answer-review-${detail.question_id}`}>
      <header className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-border bg-muted/30">
        <span className="text-[13px] font-semibold">Q{(index ?? 0) + 1}</span>
        <span className="text-[11px] text-muted-foreground">{typeLabel(detail.question_type)}</span>
        {detail.topic && <span className="text-[11px] text-muted-foreground">· {detail.topic}</span>}
        {detail.difficulty && <span className="rounded px-1.5 h-5 inline-flex items-center text-[10px] font-semibold uppercase bg-muted text-muted-foreground">{detail.difficulty}</span>}
        <span className="ml-auto flex items-center gap-2">
          {unanswered ? (
            <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-muted text-muted-foreground"><Minus className="w-3 h-3" />Not answered</span>
          ) : detail.is_correct ? (
            <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3" weight="fill" />Correct</span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-red-100 text-red-800"><XCircle className="w-3 h-3" weight="fill" />Incorrect</span>
          )}
          <span className="text-[12px] font-medium tabular-nums">{got}/{max} {max === 1 ? 'mark' : 'marks'}</span>
        </span>
      </header>

      <div className="p-3 space-y-3">
        <RichHtml html={detail.question_text} className="text-[14px] font-medium" />
        {detail.media_url && <img src={detail.media_url} alt="" className="max-h-72 rounded border border-border" />}

        {choices ? (
          <ul className="space-y-1.5" data-testid="review-options">
            {detail.options.map((o) => <OptionRow key={o.id || o.label} option={o} />)}
          </ul>
        ) : (
          <TextAnswers detail={detail} />
        )}

        {choices && unanswered && <p className="text-[12px] text-muted-foreground italic">You did not answer this question.</p>}

        {detail.explanation && (
          <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2" data-testid="review-explanation">
            <p className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-sky-800 mb-1"><Info className="w-3.5 h-3.5" />Explanation</p>
            <RichHtml html={detail.explanation} className="text-[13px] text-sky-950" />
          </div>
        )}
        {detail.hint && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800 mb-1"><Lightbulb className="w-3.5 h-3.5" />Hint</p>
            <RichHtml html={detail.hint} className="text-[13px] text-amber-950" />
          </div>
        )}
      </div>
    </article>
  );
}
